import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowLeft, Calendar, Mail } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { supabase } from "@/integrations/supabase/client";

const PaymentSuccess = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const customerDataParam = searchParams.get("customer_data");
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingLead, setIsProcessingLead] = useState(false);

  useEffect(() => {
    const processPaymentSuccess = async () => {
      if (sessionId && customerDataParam) {
        try {
          setIsProcessingLead(true);
          const customerData = JSON.parse(decodeURIComponent(customerDataParam));
          
          // Call the process-payment-success function to create lead and send emails
          const { error } = await supabase.functions.invoke('process-payment-success', {
            body: {
              sessionId,
              customerData
            }
          });

          if (error) {
            console.error('Error processing payment success:', error);
          }
        } catch (error) {
          console.error('Error parsing customer data or processing payment:', error);
        } finally {
          setIsProcessingLead(false);
        }
      }
      
      // Brief loading period
      const timer = setTimeout(() => setIsLoading(false), 1000);
      return () => clearTimeout(timer);
    };

    processPaymentSuccess();
  }, [sessionId, customerDataParam]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verificando pago...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full bg-card border-primary/20">
        <CardContent className="p-8 text-center">
          <div className="mb-6">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-primary mb-2">
              ¡Pago Realizado con Éxito!
            </h1>
            <p className="text-lg text-muted-foreground">
              Tu campaña publicitaria ha sido confirmada
            </p>
          </div>

          <div className="bg-primary/10 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Detalles de tu compra:</h2>
            <div className="space-y-2 text-left">
              <div className="flex justify-between">
                <span>Producto:</span>
                <span className="font-semibold">Campaña Publicitaria Local</span>
              </div>
              <div className="flex justify-between">
                <span>Duración:</span>
                <span className="font-semibold">7 días</span>
              </div>
              <div className="flex justify-between">
                <span>Precio:</span>
                <span className="font-semibold text-primary">130€</span>
              </div>
              {sessionId && (
                <div className="flex justify-between text-sm">
                  <span>ID de sesión:</span>
                  <span className="font-mono">{sessionId.substring(0, 20)}...</span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-muted/50 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold mb-3 flex items-center justify-center">
              <Calendar className="h-5 w-5 mr-2" />
              Próximos Pasos
            </h3>
            <div className="space-y-3 text-left">
              <div className="flex items-start">
                <Mail className="h-4 w-4 mr-2 mt-1 text-primary" />
                <div>
                  <p className="font-medium">Confirmación por email</p>
                  <p className="text-sm text-muted-foreground">
                    Recibirás un email de confirmación con todos los detalles
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-4 w-4 mr-2 mt-1 text-primary" />
                <div>
                  <p className="font-medium">Contacto en 24 horas</p>
                  <p className="text-sm text-muted-foreground">
                    Nuestro equipo se pondrá en contacto contigo para configurar tu campaña
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button variant="outline" className="w-full sm:w-auto">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver al inicio
              </Button>
            </Link>
            <Link to="/reservar-cita">
              <Button className="gradient-gold hover-lift w-full sm:w-auto">
                <Calendar className="h-4 w-4 mr-2" />
                Agendar consultoría
              </Button>
            </Link>
          </div>

          <div className="mt-6 p-4 bg-accent/10 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>¿Necesitas ayuda?</strong> Contacta con nosotros en{" "}
              <a href="mailto:hola@mktn-guardians.com" className="text-primary hover:underline">
                hola@mktn-guardians.com
              </a>{" "}
              o llámanos al{" "}
              <a href="tel:+34666777888" className="text-primary hover:underline">
                +34 666 777 888
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;